import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const options = { withCredentials: true, timeout: 20000 };
const enc = encodeURIComponent;

export default function VideoEvidenceReview({ pupilId }) {
  const [rows, setRows] = useState([]);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const epoch = useRef(0);
  const inFlight = useRef(false);

  const list = async (generation) => {
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(`${API}/evidence/video-reviews/pupil/${enc(pupilId)}`, options);
      if (generation !== epoch.current) return;
      if (!Array.isArray(data.reviews)) throw new Error("Invalid review list");
      setRows(data.reviews);
    } catch (_) {
      if (generation === epoch.current) setError("Video reviews could not be loaded. Please retry.");
    } finally { if (generation === epoch.current) setLoading(false); }
  };

  useEffect(() => {
    const generation = ++epoch.current;
    setRows([]); setReview(null); setMessage(""); setBusy(false); inFlight.current = false;
    if (pupilId) list(generation);
    else setLoading(false);
    return () => { epoch.current += 1; };
    // Reset and refetch only when the pupil changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pupilId]);

  const open = async (id) => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError(""); setReview(null); setMessage("");
    const generation = epoch.current;
    try {
      const { data } = await axios.get(`${API}/evidence/${enc(id)}/video-review`,
        { ...options, params: { pupil_id: pupilId } });
      if (generation !== epoch.current) return;
      if (data.evidence_id !== id || !data.review_id || !data.analysis) throw new Error("Invalid review");
      setReview(data);
    } catch (_) {
      if (generation === epoch.current) setError("This review could not be opened. Select it again to retry.");
    } finally {
      if (generation === epoch.current) { inFlight.current = false; setBusy(false); }
    }
  };

  const prepare = async () => {
    if (!review || inFlight.current) return;
    inFlight.current = true; setBusy(true); setError(""); setMessage("");
    const generation = epoch.current;
    try {
      const { data } = await axios.post(`${API}/evidence/${enc(review.evidence_id)}/prepare-video-homework`,
        { pupil_id: pupilId, review_id: review.review_id }, options);
      if (generation !== epoch.current) return;
      if (data.evidence_id !== review.evidence_id) throw new Error("Invalid homework receipt");
      setReview(current => ({ ...current, confirmed_for_learning: true }));
      if (!data.homework_entry_id || data.status === "pending") {
        setMessage("Review confirmed. Homework preparation is pending; retry to check it.");
        return;
      }
      const result = await axios.post(`${API}/homework-content/promote/entry/${enc(data.homework_entry_id)}`, {}, options);
      if (generation !== epoch.current) return;
      if (result.data?.id !== data.homework_entry_id || result.data?.status !== "content_ready") throw new Error("Content not ready");
      const receipt = result.data.pupil_homework_receipt;
      if (receipt?.status === "saved_to_pupil_homework") {
        setMessage(receipt.active_count > 0
          ? "Homework is saved in the pupil’s homework page. This does not mean they have read it yet."
          : "This homework is already completed or dismissed. It has not been reopened.");
      } else {
        setMessage("Homework content is ready. Saving it to the pupil’s homework has not been verified; retry to check.");
      }
    } catch (_) {
      if (generation === epoch.current) setError("Homework preparation was not confirmed. Retry to recover; your saved review is retained.");
    } finally {
      if (generation === epoch.current) { inFlight.current = false; setBusy(false); }
    }
  };

  if (!pupilId) return null;
  return <section className="border-t border-slate-700 py-4" aria-label="Video evidence reviews">
    <h2 className="font-semibold">Video evidence reviews</h2>
    {loading ? <p role="status">Loading video reviews…</p> : <>
      {!rows.length && !error && <p>No video reviews are ready for this pupil.</p>}
      <ul>{rows.map((row, index) => <li key={row.id}>
        <button type="button" disabled={busy} onClick={() => open(row.id)} className="py-2 underline">
          Open video review {index + 1}
        </button>
      </li>)}</ul>
    </>}
    {error && <p role="alert">{error}</p>}
    {!loading && !review && <button type="button" disabled={busy} onClick={() => list(epoch.current)}>Refresh reviews</button>}
    {review && <div className="space-y-3 pt-3">
      <p>{review.learning_focus?.competency_label || "Lesson review"}</p>
      <p>{review.analysis.summary}</p>
      <h3 className="font-semibold">Observations</h3>
      <ul>{(review.analysis.observations || []).map((item, i) => <li key={i}>{item.kind}: {item.text}</li>)}</ul>
      <h3 className="font-semibold">What the footage cannot establish</h3>
      <ul>{(review.analysis.evidence_limitations || []).map((text, i) => <li key={i}>{text}</li>)}</ul>
      <h3 className="font-semibold">Suggested next steps</h3>
      <ul>{(review.analysis.recommendations || []).map((text, i) => <li key={i}>{text}</li>)}</ul>
      <p>These AI observations are advisory. Confirming them does not certify practical competence.</p>
      <p>Check the observations and limitations before preparing homework for this pupil.</p>
      <button type="button" disabled={busy} onClick={prepare} className="py-3 underline">
        {busy ? "Preparing…" : review.confirmed_for_learning ? "Prepare or retry homework" : "Confirm review and prepare homework"}
      </button>
    </div>}
    {message && <p role="status">{message}</p>}
  </section>;
}
